import {
    ArchiveRegularIcon,
    CopyRegularIcon,
    PencilOutlineIcon,
    StarRegularIcon,
    TrashRegularIcon,
    UndoRegularIcon,
} from 'shells/icon';
import type {ActionMenuOptions, ToolbarOption, Props as ToolbarProps} from 'shells/toolbar/toolbar';

type ToolbarActionHandlers = {
    onMarkAsPrimary?: () => Promise<any>;
    onChangeFormerStatus?: (isFormer: boolean) => Promise<any>;
    onEdit: () => void;
    onDelete: () => void;
    onDeleteRef?: (el?: HTMLElement | null | undefined) => void;
    onCopy: (isCopied: boolean) => void;
};

type FormerPrimaryParams = {
    isFormer?: boolean;
    canMarkAsPrimary?: boolean;
    canChangeFormerStatus?: boolean;
};

export function getBusinessCardRowToolbarActions(
    formerPrimaryParams: FormerPrimaryParams,
    toolbarActionHandlers: ToolbarActionHandlers,
    value: string,
    isCopied: boolean,
    setIsMutating?: (isMutating: boolean) => void
): ToolbarProps {
    const {isFormer = false, canMarkAsPrimary, canChangeFormerStatus} = formerPrimaryParams;

    // Avoid duplication of state setting by wrapping the promises in a function
    // that handles the state setting before and after the promise is resolved
    const withMutatingState = (fn: () => Promise<any>) => {
        setIsMutating?.(true);
        fn().finally(() => setIsMutating?.(false));
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(value ?? '').then(() => {
            toolbarActionHandlers.onCopy?.(true);
            setTimeout(() => {
                toolbarActionHandlers.onCopy?.(false);
            }, 2000);
        });
    };

    const actionMenuOptions: ActionMenuOptions = {
        options: [],
        getRef: toolbarActionHandlers.onDeleteRef,
    };
    const toolbarOptions: ToolbarOption[] = [];

    if (canMarkAsPrimary && toolbarActionHandlers.onMarkAsPrimary) {
        actionMenuOptions.options.push({
            key: 'mark-as-primary',
            icon: <StarRegularIcon size={20} />,
            label: 'Mark as primary',
            handleSelect: () => withMutatingState(() => toolbarActionHandlers.onMarkAsPrimary!()),
        });
    }

    if (canChangeFormerStatus && toolbarActionHandlers.onChangeFormerStatus && !isFormer) {
        actionMenuOptions.options.push({
            key: 'change-former-status',
            icon: <ArchiveRegularIcon size={20} />,
            label: 'Mark as former',
            handleSelect: () =>
                withMutatingState(() => toolbarActionHandlers.onChangeFormerStatus!(true)),
        });
    }

    actionMenuOptions.options.push({
        key: 'delete',
        icon: <TrashRegularIcon size={20} wrapWithDiv={false} />,
        label: 'Delete',
        handleSelect: () => toolbarActionHandlers.onDelete(),
    });

    if (canChangeFormerStatus && toolbarActionHandlers.onChangeFormerStatus && isFormer) {
        toolbarOptions.push({
            id: 'change-former-status',
            icon: UndoRegularIcon,
            tooltipText: 'Mark as current',
            onClick: () =>
                withMutatingState(() => toolbarActionHandlers.onChangeFormerStatus!(false)),
        });
    }

    if (!isFormer) {
        toolbarOptions.push({
            id: 'edit',
            icon: PencilOutlineIcon,
            tooltipText: 'Edit',
            onClick: () => toolbarActionHandlers.onEdit(),
        });
    }

    toolbarOptions.push({
        id: 'copy',
        icon: CopyRegularIcon,
        tooltipText: 'Copy',
        onClick: () => (isCopied ? undefined : handleCopy()),
        getRef: toolbarActionHandlers.onDeleteRef,
    });

    return {options: toolbarOptions, actionMenuOptions};
}
